import { z } from 'zod';
import { BusinessContextSchema, ReportIDSchema } from './BusinessAnalysisSchemas.js';
import { EntityReportSpecificationSchema } from './ReportSpecificationSchemas.js';
/**
 * Zod schemas for Storage Overlay types.
 *
 * PURPOSE:
 *
 * These schemas define the wire format for localStorage overlay entries that track
 * local modifications to server-provided report specifications. Each entry is either:
 * - A `spec` variant: Contains a modified/new report specification
 * - A `deleted` variant: Marks a server-provided report as soft-deleted locally
 *
 * ARCHITECTURE:
 *
 * - **Zod Types (this file)**: DTOs for serialization boundaries
 *   - Used for localStorage YAML serialization
 *   - Used for YAML export (including deletion markers for Part 5.2)
 *
 * - **Domain Types** (@multiplier/lib-platform-business-analysis):
 *   - `StorageOverlayEntry` discriminated union
 *   - Used for React state in the hook
 *   - Converted to/from Zod DTOs via Injection
 *
 * USAGE:
 *
 * ```typescript
 * // Read from localStorage: YAML string → Zod DTO → Domain
 * const yamlString = localStorage.getItem(key);
 * const zodEntry = ZodStorageOverlayEntrySchema.parse(yaml.load(yamlString));
 * const entry = storageOverlayEntryInjection.invert(zodEntry).getOrThrow();
 *
 * // Write to localStorage: Domain → Zod DTO → YAML string
 * const zodEntry = storageOverlayEntryInjection.apply(entry);
 * const yamlString = yaml.dump(zodEntry);
 * localStorage.setItem(key, yamlString);
 * ```
 */
/**
 * Storage entry for a modified or locally-created report specification.
 * The `spec` field contains the full report specification.
 */
export const ZodStorageOverlayEntrySpecSchema = z
    .object({
    type: z.literal('spec'),
    version: z.string().describe('Storage format version'),
    timestamp: z.string().describe('ISO timestamp of when this entry was created/modified'),
    spec: EntityReportSpecificationSchema.describe('The report specification'),
})
    .strict();
/**
 * Storage entry marking a server-provided report as soft-deleted locally.
 * When this entry exists, the report is hidden from the UI but can be restored
 * by removing the entry (via "Reset to Defaults").
 */
export const ZodStorageOverlayEntryDeletedSchema = z
    .object({
    type: z.literal('deleted'),
    version: z.string().describe('Storage format version'),
    timestamp: z.string().describe('ISO timestamp of when this entry was deleted'),
})
    .strict();
/**
 * Legacy storage entry format (before discriminated union).
 * Entries without `type` field are treated as `spec` variant for backward compatibility.
 */
export const ZodStorageOverlayEntryLegacySchema = z.object({
    version: z.string().optional(),
    timestamp: z.string(),
    spec: EntityReportSpecificationSchema,
});
/**
 * Discriminated union of storage overlay entry variants.
 * Includes legacy format for backward compatibility.
 */
export const ZodStorageOverlayEntrySchema = z.union([
    z.discriminatedUnion('type', [ZodStorageOverlayEntrySpecSchema, ZodStorageOverlayEntryDeletedSchema]),
    ZodStorageOverlayEntryLegacySchema,
]);
/**
 * Nested map structure for serializing the full local storage state.
 * Structure: BusinessContext → ReportID → StorageOverlayEntry
 *
 * Used for:
 * - YAML export with deletion markers (Part 5.2)
 * - Potential future bulk storage operations
 */
export const ZodStorageOverlayMapSchema = z.record(BusinessContextSchema, z.record(ReportIDSchema, ZodStorageOverlayEntrySchema));
/**
 * File-level schema for YAML export of local edits.
 * Structure: { Edits: { [businessContext]: { [reportId]: StorageOverlayEntry } } }
 *
 * Used for:
 * - Exporting local changes from the dashboard to YAML
 * - Importing changes via CLI merge command
 *
 * The `Edits` key distinguishes this format from the older `ReportSpecificationFileSchema`
 * which used `BusinessContexts` and contained only specs (no deletion markers).
 */
export const ReportSpecificationEditsFileSchema = z
    .object({
    Edits: ZodStorageOverlayMapSchema.describe('Map of business contexts to report edits'),
})
    .strict();
//# sourceMappingURL=StorageOverlaySchemas.js.map